<template>
  <div class="flex h-screen bg-[#f9fafb]">
    <!-- Sidebar desktop -->
    <SidebarComponent />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Header desktop -->
      <HeaderComponent class="hidden" />

      <main
        ref="mainContentRef"
        class="flex-1 p-10 overflow-y-auto"
      >
        <!-- Banner -->
        <section
          class="bg-[#BFDBFE] rounded-2xl p-4 sm:p-6 text-center mb-6 sm:mb-8 relative"
        >
          <h2 class="text-lg sm:text-xl font-semibold text-[#111827]">
            What’s the lesson for today?
          </h2>
          <div class="mt-4 relative w-full max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search Topics"
              class="w-full py-2 px-4 pl-10 rounded-full text-sm border border-transparent focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              @focus="handleSearchTap"
              @click="handleSearchTap"
            >
            <i
              class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <div class="absolute right-4 top-1/2 -translate-y-1/2">
              <button
                type="button"
                class="relative w-6 h-6 text-gray-400 hover:text-gray-600"
                aria-label="Open filters"
                @click="handleFilterModalOpen"
              >
                <i class="fas fa-sliders-h text-[16px]" />
                <span
                  v-if="filterCount > 0"
                  class="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-[4px] rounded-full bg-[#2563EB] text-white text-[10px] leading-[16px] text-center"
                >
                  {{ filterCount }}
                </span>
              </button>
            </div>
          </div>
        </section>

        <!-- Subjects (mobile pills, multi-select, sync with ?subjectIds=1,2,3) -->
        <div class="bg-transparent rounded-2xl mb-6">
          <div class="flex justify-between items-center mb-4">
            <div class="flex items-center gap-3">
              <h3 class="text-base font-semibold text-gray-900">
                Subjects
              </h3>
            </div>
            <div class="flex items-center gap-2">
              <!-- Hanya 'View All' (dropdown privacy dihapus) -->
              <div class="flex gap-2">
                <button
                  type="button"
                  class="toggle-pill"
                  :class="
                    privacyFilter === 'public'
                      ? 'toggle-pill-active'
                      : 'toggle-pill-idle'
                  "
                  title="Show public quizzes"
                  @click="selectPrivacy('public')"
                >
                  <i class="fas fa-globe text-xs" />
                  <span>Public</span>
                </button>

                <button
                  type="button"
                  class="toggle-pill"
                  :class="
                    privacyFilter === 'private'
                      ? 'toggle-pill-active'
                      : 'toggle-pill-idle'
                  "
                  title="Show private quizzes"
                  @click="selectPrivacy('private')"
                >
                  <i class="fas fa-lock text-xs" />
                  <span>Private</span>
                </button>
              </div>
              <a
                href="/subject-all"
                class="text-sm text-blue-600 font-medium hidden"
              >View All</a>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <!-- All chip: clear all selections -->
            <button
              type="button"
              :class="[
                'px-4 py-2 rounded-full border text-sm whitespace-nowrap transition flex items-center gap-2',
                selectedSubjectIds.length === 0
                  ? 'bg-[#2563EB] text-white border-[#2563EB]'
                  : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50',
              ]"
              title="Show all subjects"
              @click="clearAllSubjects"
            >
              <span>All</span>
            </button>

            <!-- Subject chips (already normalized) -->
            <button
              v-for="s in normalizedSubjects"
              :key="s.id"
              type="button"
              :class="[
                'px-4 py-2 rounded-full border text-sm whitespace-nowrap transition flex items-center gap-2',
                isActiveSubjectId(s.id)
                  ? 'bg-[#2563EB] text-white border-[#2563EB]'
                  : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50',
              ]"
              @click="toggleSubject(s.id)"
            >
              <span>{{ s.name }}</span>
              <span
                v-if="isActiveSubjectId(s.id)"
                class="chip-x cursor-pointer"
                aria-label="Remove subject"
                title="Remove"
                @click.stop="removeSubjectId(s.id)"
              >×</span>
            </button>
          </div>
        </div>

        <!-- ===== Privacy Pills + Section Header (NEW) ===== -->
        <div class="mb-4 sm:mb-6 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-900">
            Quizzes
          </h3>
        </div>
        <!-- ===== /Privacy Pills ===== -->

        <!-- Quiz Cards -->
        <section
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6"
        >
          <QuizCard
            v-for="(quiz, index) in quizzes"
            :id="quiz.id"
            :key="index"
            :image="quiz.image"
            :title="quiz.title"
            :description="quiz.description"
            :students="quiz.submittedCount"
            :points="quiz.CreditAmount"
            :modules="quiz.totalQuiz"
            :duration="quiz.totalHour"
          />
          <div
            v-if="loading"
            class="text-center py-4 text-gray-600"
          >
            Loading more quizzes...
          </div>
        </section>
      </main>
    </div>

    <!-- =============== Filter Modal (new look) =============== -->
    <div
      v-if="filterModalOpen"
      class="fixed inset-0 z-50 bg-black/30 flex justify-center items-end lg:items-center"
    >
      <div
        class="bg-white w-full max-w-md rounded-t-2xl lg:rounded-2xl p-0 overflow-hidden"
      >
        <!-- grabber -->
        <div class="lg:hidden pt-3">
          <div class="w-16 h-1.5 bg-gray-300 rounded-full mx-auto" />
        </div>

        <!-- Header -->
        <div class="px-6 pt-4 pb-2 flex items-center justify-between">
          <h3 class="font-semibold text-[15px]">
            Filters
          </h3>
          <button
            class="text-sm text-[#2563EB] font-medium"
            @click="resetFilter()"
          >
            Reset
          </button>
        </div>

        <hr class="border-gray-100">

        <!-- Progress -->
        <div class="px-6 py-4">
          <div class="flex items-center gap-2 mb-3">
            <i class="fas fa-chart-bar text-gray-500" />
            <h4 class="text-sm font-semibold">
              Progres
            </h4>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="opt in progressOptions"
              :key="opt"
              :class="['chip', progress === opt ? 'chip-active' : 'chip-idle']"
              @click="progress = opt"
            >
              <span>{{ opt }}</span>
              <span
                v-if="progress === opt"
                class="chip-x"
              >×</span>
            </button>
          </div>
        </div>

        <hr class="border-gray-100">

        <!-- Category -->
        <div class="px-6 py-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-cubes text-gray-500" />
              <h4 class="text-sm font-semibold">
                Category ({{ categories.length || 0 }})
              </h4>
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              v-for="cat in categories"
              :key="cat.id"
              :class="[
                'chip',
                selectedCategories.includes(cat.id)
                  ? 'chip-active'
                  : 'chip-idle',
              ]"
              @click="toggleCategory(cat.id)"
            >
              <span>{{ cat.name }}</span>
              <span
                v-if="selectedCategories.includes(cat.id)"
                class="chip-x"
              >×</span>
            </button>
          </div>
        </div>

        <hr class="border-gray-100">

        <!-- Grade -->
        <div class="px-6 pt-4 pb-6">
          <div class="flex items-center gap-2 mb-3">
            <i class="fas fa-book text-gray-500" />
            <h4 class="text-sm font-semibold">
              Grade
            </h4>
          </div>

          <!-- Custom dropdown -->
          <div
            ref="gradeMenuRef"
            class="relative"
          >
            <button
              class="w-full h-11 px-4 text-sm rounded-xl border transition flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#2563EB] border-[#CBD5E1]"
              @click="gradeOpen = !gradeOpen"
            >
              <span
                v-if="!selectedGradeText"
                class="text-gray-500"
              >Select grade</span>
              <span
                v-else
                class="text-gray-900"
              >{{ selectedGradeText }}</span>
              <i
                :class="[
                  'fas',
                  'fa-chevron-' + (gradeOpen ? 'up' : 'down'),
                  'text-gray-400',
                ]"
              />
            </button>

            <!-- options -->
            <div
              v-if="gradeOpen"
              class="absolute z-10 mt-2 w-full bg-white border rounded-xl shadow-sm max-h-64 overflow-auto"
            >
              <button
                v-for="g in grades"
                :key="g?.Id ?? g?.id"
                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                @click="selectGrade(g?.Id ?? g?.id)"
              >
                {{ g?.GradeName }}
              </button>
            </div>
          </div>
        </div>

        <div class="px-6 pb-6">
          <button
            class="w-full bg-[#2563EB] hover:bg-[#1E40AF] text-white h-11 rounded-full text-sm font-semibold"
            @click="applyFilter()"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
    <!-- =============== /Filter Modal =============== -->
  </div>
</template>

<script setup>
import { useQuizGradeAll } from "@/stores/gradeStore";
import SidebarComponent from "@/components/base/SidebarComponent.vue";
import HeaderComponent from "@/components/base/HeaderComponent.vue";
import QuizCard from "@/components/base/QuizCardComponent.vue";
import { useAuthStore } from "@/stores/authStore";
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { QuizListSubject } from "@/api/subjectApi";
import { AllQuizList, QuizCategories } from "@/api/quizApi";

const mainContentRef = ref(null);
const quizzes = ref([]);
const allSubjects = ref([]);
const categories = ref([]);
const subjects = ref([]);
const filterModalOpen = ref(false);

// privacy filter (dropdown dihapus dari UI, state & handler tetap)
const privacyMenuOpen = ref(false);
const privacyMenuRef = ref(null);
const privacyFilter = ref("public");

// progress
const progress = ref("All");
const progressOptions = ["All", "Ongoing", "Completed"];

// categories
const selectedCategories = ref([]);

// grade dropdown
const grade = ref("");
const gradeOpen = ref(false);
const gradeMenuRef = ref(null);
const grades = ref([{ id: 1, GradeName: "Loading..." }]);
const selectedGradeText = computed(() => {
  const g = grades.value?.find((x) => (x?.Id ?? x?.id) === grade.value);
  return g?.GradeName || "";
});

// ===== Utilities =====
const toNum = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const isQuizArchived = (quiz) => {
  const flag =
    quiz?.isArchived ??
    quiz?.IsArchived ??
    quiz?.archived ??
    quiz?.Archived ??
    null;
  if (flag == null) return false;
  if (typeof flag === "boolean") return flag;
  if (typeof flag === "number") return flag === 1;
  if (typeof flag === "string") {
    const normalized = flag.trim().toLowerCase();
    return ["1", "true", "yes", "y"].includes(normalized);
  }
  return Boolean(flag);
};

const extractSubjectIdsFromQuery = (query = {}) => {
  const ids = [];
  const addId = (value) => {
    if (value == null) return;
    const normalized = typeof value === "string" ? value.trim() : value;
    if (normalized === "" || normalized == null) return;
    const n = toNum(normalized);
    if (n != null && !ids.includes(n)) ids.push(n);
  };

  const rawIds = query.subjectIds;
  if (typeof rawIds === "string") {
    rawIds.split(",").forEach(addId);
  } else if (Array.isArray(rawIds)) {
    rawIds.forEach(addId);
  } else if (rawIds != null) {
    addId(rawIds);
  }

  addId(query.subjectId);

  return ids;
};

const subjectListsMatch = (next, prev) =>
  next.length === prev.length && next.every((id, index) => id === prev[index]);

// ===== SUBJECT MULTI SELECT =====
const selectedSubjectIds = ref([]);

// Normalisasi subject agar stabil { id:number, name:string }
const normalizedSubjects = computed(() => {
  return (subjects.value ?? [])
    .map((s) => {
      const id =
        toNum(s?.Id) ??
        toNum(s?.id) ??
        toNum(s?.SubjectId) ??
        toNum(s?.subjectId);
      const name =
        s?.SubjectName ?? s?.name ?? s?.Name ?? s?.title ?? s?.Title ?? "";
      return id != null && name ? { id, name } : null;
    })
    .filter(Boolean);
});

// filter counter
const filterCount = computed(() => {
  let c = 0;
  if (privacyFilter.value !== "public") c += 1;
  if (selectedCategories.value.length) c += 1;
  if (progress.value && progress.value !== "All") c += 1;
  if (grade.value) c += 1;
  if (selectedSubjectIds.value.length) c += 1;
  return c;
});

const isActiveSubjectId = (id) => selectedSubjectIds.value.includes(toNum(id));

function toggleSubject(id) {
  const nid = toNum(id);
  if (nid == null) return;
  const idx = selectedSubjectIds.value.indexOf(nid);
  if (idx >= 0) selectedSubjectIds.value.splice(idx, 1);
  else selectedSubjectIds.value.push(nid);
  syncSubjectIdsToRoute();
  resetPaginationAndFetch();
}

function removeSubjectId(id) {
  const nid = toNum(id);
  if (nid == null) return;
  selectedSubjectIds.value = selectedSubjectIds.value.filter((x) => x !== nid);
  syncSubjectIdsToRoute();
  resetPaginationAndFetch();
}

function clearAllSubjects() {
  if (selectedSubjectIds.value.length === 0) return;
  selectedSubjectIds.value = [];
  syncSubjectIdsToRoute();
  resetPaginationAndFetch();
}

function selectPrivacy(value) {
  if (!value || value === privacyFilter.value) return;
  privacyFilter.value = value;
  privacyMenuOpen.value = false;
  syncPrivacyToRoute();
  resetPaginationAndFetch();
}

function syncPrivacyToRoute() {
  const q = { ...route.query };
  if (privacyFilter.value && privacyFilter.value !== "public") {
    q.privacy = privacyFilter.value;
  } else {
    delete q.privacy;
  }
  router.replace({ name: route.name, query: q }).catch(() => {});
}

// stores & router
const quizStore = useQuizGradeAll();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

let isNavigatingToSearch = false;

const handleSearchTap = (event) => {
  if (isNavigatingToSearch) return;

  isNavigatingToSearch = true;

  event?.preventDefault?.();
  event?.target?.blur?.();

  router.push({ name: "QuizSearch" }).finally(() => {
    setTimeout(() => {
      isNavigatingToSearch = false;
    }, 200);
  });
};

// Pagination
const page = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const loading = ref(false);

const getAllQuiz = async (isLoadMore = false) => {
  if (loading.value || (isLoadMore && page.value > totalPages.value)) return;
  loading.value = true;

  try {
    const params = {
      userId: auth.userId ? auth.userId : undefined,
      page: page.value,
      pageSize: pageSize.value,
      isPublic: privacyFilter.value === "public",
    };

    if (selectedCategories.value.length > 0) {
      params.quizCategoryIds = selectedCategories.value;
    }
    if (grade.value) {
      params.gradeId = grade.value;
    }
    // subject filter (multiple IDs)
    if (selectedSubjectIds.value.length > 0) {
      params.quizSubjectIds = selectedSubjectIds.value
        .map(toNum)
        .filter((n) => n != null);
    }

    const data = await AllQuizList(params);

    const list = (data?.data ?? [])
      .filter((quiz) => (quiz?.totalQuiz ?? 0) > 0)
      .filter((quiz) => !isQuizArchived(quiz));
    if (!isLoadMore) {
      quizzes.value = list;
    } else {
      quizzes.value.push(...list);
    }

    totalPages.value = data?.totalPages ?? totalPages.value;
    page.value++;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const getAllSubject = async () => {
  try {
    const data = await QuizListSubject();
    allSubjects.value = data;
    subjects.value = data;
  } catch (error) {
    console.error(error);
  }
};

function syncSubjectIdsToRoute() {
  const q = { ...route.query };
  delete q.subjectId;
  delete q.subjectName;
  if (selectedSubjectIds.value.length) {
    q.subjectIds = selectedSubjectIds.value.join(",");
  } else {
    delete q.subjectIds;
  }
  router.replace({ name: route.name, query: q }).catch(() => {});
}

function resetPaginationAndFetch() {
  page.value = 1;
  totalPages.value = 1;
  quizzes.value = [];
  getAllQuiz(false);
}

// Infinite Scroll
const handleScroll = () => {
  const el = mainContentRef.value;
  if (!el) return;

  const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
  if (isNearBottom) {
    getAllQuiz(true);
  }
};

onMounted(() => {
  selectedSubjectIds.value = extractSubjectIdsFromQuery(route.query);

  const privacyQuery =
    typeof route.query.privacy === "string"
      ? route.query.privacy.toLowerCase()
      : "";
  if (privacyQuery === "private" || privacyQuery === "public") {
    privacyFilter.value = privacyQuery;
  } else if (route.query.privacy != null) {
    privacyFilter.value = "public";
    syncPrivacyToRoute();
  }

  getAllQuiz();
  getCategories();
  getAllSubject();
  if (mainContentRef.value) {
    mainContentRef.value.addEventListener("scroll", handleScroll);
  }
  document.addEventListener("click", onClickAway);
});

onBeforeUnmount(() => {
  if (mainContentRef.value) {
    mainContentRef.value.removeEventListener("scroll", handleScroll);
  }
  document.removeEventListener("click", onClickAway);
});

// react when subject-related query params change externally
watch(
  () => [route.query.subjectIds, route.query.subjectId],
  (newVal, oldVal) => {
    if (newVal?.[0] === oldVal?.[0] && newVal?.[1] === oldVal?.[1]) return;
    const next = extractSubjectIdsFromQuery(route.query);
    if (subjectListsMatch(next, selectedSubjectIds.value)) return;
    selectedSubjectIds.value = next;
    resetPaginationAndFetch();
  }
);

watch(
  () => route.query.privacy,
  (newVal, oldVal) => {
    if (newVal === oldVal) return;
    const normalized =
      typeof newVal === "string" ? newVal.toLowerCase() : undefined;
    const allowed = ["public", "private"];
    const next = allowed.includes(normalized) ? normalized : "public";
    if (privacyFilter.value === next) {
      if (!allowed.includes(normalized) && route.query.privacy != null) {
        syncPrivacyToRoute();
      }
      return;
    }
    privacyFilter.value = next;
    privacyMenuOpen.value = false;
    if (!allowed.includes(normalized) && route.query.privacy != null) {
      syncPrivacyToRoute();
    }
    resetPaginationAndFetch();
  }
);

const onClickAway = (e) => {
  if (gradeMenuRef.value && !gradeMenuRef.value.contains(e.target)) {
    gradeOpen.value = false;
  }
  if (privacyMenuRef.value && !privacyMenuRef.value.contains(e.target)) {
    privacyMenuOpen.value = false;
  }
};

const getCategories = async () => {
  try {
    const res = await QuizCategories();
    categories.value = res;
  } catch (err) {
    console.error("Gagal mengambil kategori:", err.message);
  }
};

const handleFilterModalOpen = async () => {
  filterModalOpen.value = true;
  try {
    const data = await quizStore.Grade();
    grades.value = data.grade;
  } catch (err) {
    console.error("Gagal mengambil grade:", err.message);
  }
};

let handling = false;
function toggleCategory(catId) {
  if (handling) return;
  handling = true;

  if (selectedCategories.value.includes(catId)) {
    selectedCategories.value = selectedCategories.value.filter(
      (id) => id !== catId
    );
  } else {
    selectedCategories.value.push(catId);
  }
  setTimeout(() => (handling = false), 0);
}

function selectGrade(id) {
  grade.value = id;
  gradeOpen.value = false;
}

function resetFilter() {
  progress.value = "All";
  selectedCategories.value = [];
  grade.value = "";
  page.value = 1;

  privacyFilter.value = "public";
  privacyMenuOpen.value = false;

  // clear subjects & URL
  selectedSubjectIds.value = [];
  const q = { ...route.query };
  delete q.subjectIds;
  delete q.subjectId;
  delete q.subjectName;
  delete q.privacy;
  router.replace({ name: route.name, query: q }).catch(() => {});

  getAllQuiz(false);
}

function applyFilter() {
  page.value = 1;
  quizzes.value = [];
  getAllQuiz(false);
  filterModalOpen.value = false;
}
</script>

<style scoped>
/* Pills for generic chips (modal) */
.chip {
  @apply inline-flex items-center gap-2 px-3 h-8 rounded-full text-sm transition border;
}
.chip-idle {
  @apply bg-white border-gray-200 text-gray-700 hover:bg-gray-50;
}
.chip-active {
  @apply bg-[#2563EB] border-[#2563EB] text-white;
}
.chip-x {
  @apply inline-flex items-center justify-center rounded-full bg-transparent px-1 leading-none;
}

/* Privacy toggle pills (di atas grid) */
.toggle-pill {
  @apply inline-flex items-center gap-2 px-4 h-8 rounded-full text-sm transition border;
}
.toggle-pill-idle {
  @apply bg-white border-gray-200 text-gray-700 hover:bg-gray-50;
}
.toggle-pill-active {
  @apply bg-[#2563EB] border-[#2563EB] text-white;
}

/* hide scrollbar for horizontal pills */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
